"use client";

import { useRef } from "react";
import {
  Button,
  Eyebrow,
  GradText,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { useEnterStagger, useMaskedReveal } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import { LANES, PROOF_CHIPS } from "./data";

/**
 * /solutions/enterprise hero — light on-grid, left-aligned (handoff design).
 * Block 1 is the claim (eyebrow → h1 → lead → CTAs → proof chips); block 2 is
 * the two-lane split-scan band that routes the champion and the procurement
 * reviewer to their sections. Page-only and always inside `RefSections onGrid`,
 * so the row-layout hooks run unconditionally.
 */
export default function EnterpriseHero() {
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const lanesRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h1", sub: "[data-hero-sub]" });
  // stagger 0: the two lanes are one visual row — rising in lockstep keeps
  // them level at every frame (a stagger reads as misaligned cards mid-flight).
  useEnterStagger(lanesRef, "[data-lane]", { y: 24, stagger: 0 });

  return (
    <SectionShell id="ent-hero" decoAvoid className="md:pt-px md:pb-px">
      <div ref={rootRef} className="pt-24 pb-4 md:pb-0 md:pt-[calc(2*var(--grid-cell,72px))]">
        {/* ── Block 1 — the claim. ─────────────────────────────────────── */}
        <div data-grid-block>
          <div ref={copyRef}>
            <div data-hero-sub className="flex">
              <Eyebrow>Enterprise</Eyebrow>
            </div>
            <h1 className="mt-[22px] max-w-[820px] text-[clamp(34px,5vw,56px)] font-extrabold leading-[1.04] tracking-[-0.03em]">
              Route <GradText>serious traffic</GradText>.
            </h1>
            <p
              data-hero-sub
              className="mt-[18px] max-w-[600px] text-[17px] leading-[1.6] text-muted [text-wrap:pretty]"
            >
              Control who touches your links, put a 99.99% uptime SLA in writing, and
              keep visitor IPs anonymised before storage — with a dedicated engineer
              who migrates your links and sees you through security review.
            </p>
            <div data-hero-sub className="mt-7 flex flex-wrap gap-3">
              <Button href={`${routePath("contact")}?intent=sales`}>Talk to sales</Button>
              <Button href={routePath("pricing")} variant="ghost">
                Compare plans
              </Button>
            </div>
            <div data-hero-sub className="mt-9 flex flex-wrap gap-2.5">
              {PROOF_CHIPS.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-line-2 bg-surface px-3.5 py-[7px] font-jetbrains text-[11.5px] text-muted"
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Block 2 — the split-scan lanes. ──────────────────────────── */}
        {/* max-md:mt-10 — on md+ the row aligner owns this block's margin;
            below md the phone stack needs its own breathing room. */}
        <div ref={lanesRef} data-grid-block className="max-md:mt-10">
          <div className="grid gap-4 md:grid-cols-2">
            {LANES.map((lane) => (
              <div
                key={lane.title}
                data-lane
                className="rounded-ref-lg border border-line bg-surface px-7 py-[26px] shadow-ref transition-[transform,box-shadow] duration-[250ms] hover:-translate-y-0.5 hover:shadow-ref-lg"
              >
                <div
                  className={`font-jetbrains text-[10.5px] font-semibold uppercase tracking-[0.14em] ${lane.accent}`}
                >
                  {lane.eyebrow}
                </div>
                <div className="mt-2.5 text-[19px] font-bold tracking-[-0.015em]">{lane.title}</div>
                <p className="mb-[18px] mt-[7px] text-sm leading-[1.55] text-muted [text-wrap:pretty]">
                  {lane.body}
                </p>
                <div className="flex gap-[18px] font-jetbrains text-xs">
                  {lane.links.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className={`${lane.accent} underline-offset-2 hover:underline`}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
